/**
 * Client-side activity log for workspace views.
 *
 * Mirrors `pppcActivityLog`: until the backend exposes a real audit trail for
 * workspace edits, we persist a per-workspace event stream in localStorage so
 * admins can see who changed nav configuration (sections, ordering, hidden
 * items, landing path) and when. Entries are append-only and capped per
 * workspace.
 *
 * The diff is computed by `diffWorkspaceNavConfig` and is intentionally
 * limited to nav-affecting fields (nav items, landing path, default flag,
 * role binding, name) — dashboard widget changes are tracked separately.
 */
import { getCurrentUserEmail } from "@/lib/auth";
import type { NavItemConfig, WorkspaceView, WorkspaceViewDraft } from "@/workspace/types";

const STORAGE_KEY = "workspace-activity-log:v1";
const MAX_ENTRIES_PER_WORKSPACE = 100;

export type WorkspaceActivityAction = "created" | "updated" | "deleted";

export interface NavItemChange {
  /** Registry id, e.g. "nav.devices". */
  id: string;
  /** Effective label after rename, when the entry survived the edit. */
  label?: string;
  /** Previous order, if the item existed before. */
  fromOrder?: number;
  /** New order, if the item still exists. */
  toOrder?: number;
}

export interface WorkspaceNavDiff {
  added: NavItemChange[];
  removed: NavItemChange[];
  reordered: NavItemChange[];
  renamed: Array<{ id: string; from: string; to: string }>;
  landingPathChanged?: { from: string; to: string };
  roleBindingChanged?: { from: string | null; to: string | null };
  defaultFlagChanged?: { from: boolean; to: boolean };
  nameChanged?: { from: string; to: string };
}

export interface WorkspaceActivityEntry {
  id: string;
  workspaceId: string;
  action: WorkspaceActivityAction;
  /** Email of the acting admin, when known. */
  actor: string | null;
  /** ISO timestamp. */
  at: string;
  /** Short human summary, e.g. "Hid Vulnerabilities, reordered 2 items". */
  summary: string;
  /** Structured diff for the nav configuration. Always present for "updated". */
  diff?: WorkspaceNavDiff;
}

type Store = Record<string, WorkspaceActivityEntry[]>;

function readStore(): Store {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota exceeded */
  }
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Compute a structured diff between the previous workspace state and the
 * draft about to be saved. Only nav-affecting fields are inspected; widget
 * and page-defaults changes are intentionally ignored here so the audit
 * stream stays focused on navigation governance.
 */
export function diffWorkspaceNavConfig(
  prev: WorkspaceView,
  next: WorkspaceViewDraft,
): WorkspaceNavDiff {
  const prevById = new Map<string, NavItemConfig>(prev.navItems.map((i) => [i.id, i]));
  const nextById = new Map<string, NavItemConfig>(next.navItems.map((i) => [i.id, i]));

  const added: NavItemChange[] = [];
  const removed: NavItemChange[] = [];
  const reordered: NavItemChange[] = [];
  const renamed: WorkspaceNavDiff["renamed"] = [];

  for (const [id, n] of nextById) {
    const p = prevById.get(id);
    if (!p) {
      added.push({ id, label: n.label, toOrder: n.order });
      continue;
    }
    if (p.order !== n.order) {
      reordered.push({ id, label: n.label ?? p.label, fromOrder: p.order, toOrder: n.order });
    }
    const prevLabel = p.label ?? "";
    const nextLabel = n.label ?? "";
    if (prevLabel !== nextLabel) {
      renamed.push({ id, from: prevLabel, to: nextLabel });
    }
  }

  for (const [id, p] of prevById) {
    if (!nextById.has(id)) {
      removed.push({ id, label: p.label, fromOrder: p.order });
    }
  }

  const diff: WorkspaceNavDiff = { added, removed, reordered, renamed };
  if (prev.landingPath !== next.landingPath) {
    diff.landingPathChanged = { from: prev.landingPath, to: next.landingPath };
  }
  if ((prev.roleName ?? null) !== (next.roleName ?? null)) {
    diff.roleBindingChanged = { from: prev.roleName ?? null, to: next.roleName ?? null };
  }
  if (prev.isDefault !== next.isDefault) {
    diff.defaultFlagChanged = { from: prev.isDefault, to: next.isDefault };
  }
  if (prev.name !== next.name) {
    diff.nameChanged = { from: prev.name, to: next.name };
  }
  return diff;
}

/** True iff the diff has at least one nav-affecting change worth recording. */
export function hasNavConfigChanges(diff: WorkspaceNavDiff): boolean {
  return (
    diff.added.length > 0 ||
    diff.removed.length > 0 ||
    diff.reordered.length > 0 ||
    diff.renamed.length > 0 ||
    Boolean(diff.landingPathChanged) ||
    Boolean(diff.roleBindingChanged) ||
    Boolean(diff.defaultFlagChanged) ||
    Boolean(diff.nameChanged)
  );
}

/** Build a short human-readable summary of the diff for log surfaces. */
export function summarizeNavDiff(diff: WorkspaceNavDiff): string {
  const parts: string[] = [];
  if (diff.added.length) parts.push(`added ${diff.added.length}`);
  if (diff.removed.length) parts.push(`removed ${diff.removed.length}`);
  if (diff.reordered.length) parts.push(`reordered ${diff.reordered.length}`);
  if (diff.renamed.length) parts.push(`renamed ${diff.renamed.length}`);
  if (diff.landingPathChanged) parts.push("changed landing page");
  if (diff.roleBindingChanged) parts.push("re-bound role");
  if (diff.defaultFlagChanged) parts.push(diff.defaultFlagChanged.to ? "marked default" : "unmarked default");
  if (diff.nameChanged) parts.push("renamed workspace");
  return parts.length ? parts.join(", ") : "no nav changes";
}

export function recordWorkspaceActivity(
  entry: Omit<WorkspaceActivityEntry, "id" | "at" | "actor"> & { at?: string; actor?: string | null },
): WorkspaceActivityEntry {
  const full: WorkspaceActivityEntry = {
    id: makeId(),
    at: entry.at ?? new Date().toISOString(),
    actor: entry.actor ?? getCurrentUserEmail(),
    workspaceId: entry.workspaceId,
    action: entry.action,
    summary: entry.summary,
    diff: entry.diff,
  };
  const store = readStore();
  const existing = store[full.workspaceId] ?? [];
  store[full.workspaceId] = [full, ...existing].slice(0, MAX_ENTRIES_PER_WORKSPACE);
  writeStore(store);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("workspace-activity-updated", { detail: { workspaceId: full.workspaceId } }),
    );
  }
  return full;
}

export function getWorkspaceActivity(workspaceId: string): WorkspaceActivityEntry[] {
  return readStore()[workspaceId] ?? [];
}

/** All workspace events across every workspace, newest-first. */
export function getAllWorkspaceActivity(): WorkspaceActivityEntry[] {
  const store = readStore();
  const out: WorkspaceActivityEntry[] = [];
  for (const entries of Object.values(store)) {
    for (const e of entries) out.push(e);
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

export function clearWorkspaceActivity(workspaceId: string) {
  const store = readStore();
  if (workspaceId in store) {
    delete store[workspaceId];
    writeStore(store);
  }
}
